/**
 * ハンバーガーメニュー
 * .ist-js__headerHamburgerMenuButton をクリックするとハンバーガーメニューが開きます
 */
import type { HeaderMenu, Link, MenuSection } from './_type';

/**
 * フォーカストラップ
 */
class FocusTrap {
  private element: HTMLElement;
  private lastFocus: HTMLElement | null = null;
  private observer: MutationObserver;
  private isActive: boolean = false;
  private keydownHandler: ((event: KeyboardEvent) => void) | null = null;

  constructor(element: HTMLElement) {
    this.element = element;
    this.observer = new MutationObserver(() => {
      if (this.isActive) {
        this.updateFocusableElements();
      }
    });
  }

  activate(): void {
    this.isActive = true;
    this.lastFocus = document.activeElement as HTMLElement;
    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden'],
    });

    this.updateFocusableElements();
  }

  deactivate(): void {
    this.isActive = false;
    this.observer.disconnect();
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = null;
    }
    this.lastFocus?.focus();
  }

  private getFocusableElements(): HTMLElement[] {
    const menuContainer = this.element.querySelector<HTMLElement>(
      '.ist-header__hamburgerMenuContainer'
    );
    if (!menuContainer) return [];

    // 全てのリンクとsummaryを一旦タブ移動不可に設定
    const focusableSelectors = [
      'a[href]:not([disabled])',
      'button:not([disabled])',
      'summary',
    ]
      .map((selector) =>
        [
          `.ist-header__hamburgerMenuBodyContentItem--01 ${selector}`,
          `.ist-header__hamburgerMenuBodyContentItem--02 ${selector}`,
          `.ist-header__hamburgerMenuBodyContentItem--03 ${selector}`,
          `.ist-header__hamburgerMenuBodyContentItem--intermediate ${selector}`,
          `.ist-header__hamburgerMenuHeader ${selector}`,
          `.ist-header__hamburgerMenuFooter ${selector}`,
        ].join(',')
      )
      .join(',');

    const allElements =
      this.element.querySelectorAll<HTMLElement>(focusableSelectors);
    allElements.forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });

    let focusableElements: HTMLElement[] = [];

    // コンテナのクラスで現在の階層を判定して、該当する要素のtabindexを有効化
    if (
      menuContainer.classList.contains(
        'ist-header__hamburgerMenuContainer--thirdLevel'
      )
    ) {
      // ヘッダー内の全てのフォーカス可能な要素を取得
      const headerElements = Array.from(
        menuContainer.querySelectorAll<HTMLElement>(
          '.ist-header__hamburgerMenuHeader a[href]:not([disabled]), ' +
            '.ist-header__hamburgerMenuHeader button:not([disabled])'
        )
      );
      const thirdLevelLinks = Array.from(
        menuContainer.querySelectorAll<HTMLElement>(
          '.ist-header__hamburgerMenuBodyContentItem--03 a[href]:not([disabled])'
        )
      );
      const bannerLinks = Array.from(
        menuContainer.querySelectorAll<HTMLElement>(
          '.ist-header__hamburgerMenuBodyContentItem--03 .ist-header__hamburgerMenuFooter a[href]:not([disabled])'
        )
      );

      headerElements.forEach((element) => {
        element.removeAttribute('tabindex');
      });
      thirdLevelLinks.forEach((link) => {
        link.removeAttribute('tabindex');
      });
      bannerLinks.forEach((link) => {
        link.removeAttribute('tabindex');
      });

      focusableElements = [
        ...headerElements,
        ...thirdLevelLinks,
        ...bannerLinks,
      ];
    } else if (
      menuContainer.classList.contains(
        'ist-header__hamburgerMenuContainer--intermediateLevel'
      )
    ) {
      // ヘッダー内の全てのフォーカス可能な要素を取得
      const headerElements = Array.from(
        menuContainer.querySelectorAll<HTMLElement>(
          '.ist-header__hamburgerMenuHeader a[href]:not([disabled]), ' +
            '.ist-header__hamburgerMenuHeader button:not([disabled])'
        )
      );
      const intermediateLinks = Array.from(
        menuContainer.querySelectorAll<HTMLElement>(
          '.ist-header__hamburgerMenuBodyContentItem--intermediate a[href]:not([disabled])'
        )
      );
      const bannerLinks = Array.from(
        menuContainer.querySelectorAll<HTMLElement>(
          '.ist-header__hamburgerMenuBodyContentItem--intermediate .ist-header__hamburgerMenuFooter a[href]:not([disabled])'
        )
      );

      headerElements.forEach((element) => {
        element.removeAttribute('tabindex');
      });
      intermediateLinks.forEach((link) => {
        link.removeAttribute('tabindex');
      });
      bannerLinks.forEach((link) => {
        link.removeAttribute('tabindex');
      });

      focusableElements = [
        ...headerElements,
        ...intermediateLinks,
        ...bannerLinks,
      ];
    } else if (
      menuContainer.classList.contains(
        'ist-header__hamburgerMenuContainer--secondLevel'
      )
    ) {
      // ヘッダー内の全てのフォーカス可能な要素を取得
      const headerElements = Array.from(
        menuContainer.querySelectorAll<HTMLElement>(
          '.ist-header__hamburgerMenuHeader a[href]:not([disabled]), ' +
            '.ist-header__hamburgerMenuHeader button:not([disabled])'
        )
      );
      const summaryElements = Array.from(
        menuContainer.querySelectorAll<HTMLElement>(
          '.ist-header__hamburgerMenuSummary'
        )
      );
      const secondLevelLinks = Array.from(
        menuContainer.querySelectorAll<HTMLElement>(
          '.ist-header__hamburgerMenuBodyContentItem--02 a[href]:not([disabled])'
        )
      );
      const bannerLinks = Array.from(
        menuContainer.querySelectorAll<HTMLElement>(
          '.ist-header__hamburgerMenuBodyContentItem--02 .ist-header__hamburgerMenuFooter a[href]:not([disabled])'
        )
      );

      headerElements.forEach((element) => {
        element.removeAttribute('tabindex');
      });
      summaryElements.forEach((summary) => {
        summary.removeAttribute('tabindex');
      });
      secondLevelLinks.forEach((link) => {
        link.removeAttribute('tabindex');
      });
      bannerLinks.forEach((link) => {
        link.removeAttribute('tabindex');
      });

      focusableElements = [
        ...headerElements,
        ...summaryElements,
        ...secondLevelLinks,
        ...bannerLinks,
      ];
    } else {
      // ヘッダー内の全てのフォーカス可能な要素を取得
      const headerElements = Array.from(
        menuContainer.querySelectorAll<HTMLElement>(
          '.ist-header__hamburgerMenuHeader a[href]:not([disabled]), ' +
            '.ist-header__hamburgerMenuHeader button:not([disabled])'
        )
      );
      const firstLevelLinks = Array.from(
        menuContainer.querySelectorAll<HTMLElement>(
          '.ist-header__hamburgerMenuBodyContentItem--01 a[href]:not([disabled])'
        )
      );

      headerElements.forEach((element) => {
        element.removeAttribute('tabindex');
      });
      firstLevelLinks.forEach((link) => {
        link.removeAttribute('tabindex');
      });

      focusableElements = [...headerElements, ...firstLevelLinks];
    }

    return focusableElements;
  }

  private updateFocusableElements(): void {
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
    }

    const focusableElements = this.getFocusableElements();
    if (!focusableElements.length) return;

    // 選択済みのメニューにフォーカスを当てる、なければ最初の要素にフォーカス
    requestAnimationFrame(() => {
      const selectedElement = focusableElements.find((element) =>
        element.hasAttribute('data-selected-menu')
      );
      if (selectedElement && focusableElements.includes(selectedElement)) {
        selectedElement.focus();
      } else {
        focusableElements[0]?.focus();
      }

      // aria-expandedをfalseに設定
      focusableElements.forEach((element) => {
        element.setAttribute('aria-expanded', 'false');
      });
    });

    this.keydownHandler = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !this.isActive) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (!activeElement) return;

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', this.keydownHandler);
  }
}

/**
 * ハンバーガーメニュー
 */
class HeaderHamburgerMenu {
  private menuData: HeaderMenu | null = null;
  private readonly elements: {
    menuContainer: HTMLElement | null;
    menuBody: HTMLElement | null;
    header: HTMLElement | null;
    spNavList: HTMLElement | null;
    hamburgerMenuContent01: HTMLElement | null;
    secondLevelContainer: HTMLElement | null;
    intermediateLevelContainer: HTMLElement | null;
    thirdLevelContainer: HTMLElement | null;
  };
  private isOpen: boolean = false;
  private scrollPosition: number = 0;
  private eventListeners: Array<{
    element: HTMLElement;
    event: string;
    handler: EventListener;
  }> = [];
  private focusTrap: FocusTrap | null = null;
  private menuId: string = '';
  private readonly jsonUrl: string; // fetch先URLを保持
  private readonly lang: string; // 言語設定

  // 各階層のIDを管理するプロパティ
  private level2Id: string = '';
  private intermediateId: string = '';
  private level3Id: string = '';

  constructor(
    jsonUrl: string = '/assets/json/header.json',
    lang: string = 'ja'
  ) {
    this.jsonUrl = jsonUrl;
    this.lang = lang;
    if (this.jsonUrl === '/assets/json/header.json') {
      // <html /> の lang 属性を取得
      const htmlElement = document.querySelector('html');
      if (htmlElement instanceof HTMLElement) {
        const lang = htmlElement.getAttribute('lang');
        if (lang === 'en') {
          this.jsonUrl = '/assets/json/header-en.json';
          this.lang = 'en';
        }
      }
    }

    // '.ist-header' が data-lang="en" を持っている場合は、jsonUrl と lang を変更
    const headerElement = document.querySelector('.ist-header');
    if (headerElement?.getAttribute('data-lang') === 'en') {
      this.jsonUrl = '/assets/json/header-en.json';
      this.lang = 'en';
    }

    this.elements = {
      menuContainer: document.querySelector(
        '.ist-header__hamburgerMenuContainer'
      ),
      menuBody: document.querySelector('.ist-header__hamburgerMenuBody'),
      header: document.querySelector('.ist-header'),
      spNavList: document.querySelector(
        '.ist-header__hamburgerMenuHeader .ist-header__row01SpNavList'
      ),
      hamburgerMenuContent01: document.querySelector(
        '.ist-header__hamburgerMenuBodyContentItem--01'
      ),
      secondLevelContainer: document.querySelector<HTMLElement>(
        '.ist-header__hamburgerMenuBodyContentItem--02'
      ),
      intermediateLevelContainer: document.querySelector<HTMLElement>(
        '.ist-header__hamburgerMenuBodyContentItem--intermediate'
      ),
      thirdLevelContainer: document.querySelector<HTMLElement>(
        '.ist-header__hamburgerMenuBodyContentItem--03'
      ),
    };

    if (
      !this.elements.hamburgerMenuContent01 ||
      !this.elements.header ||
      !this.elements.secondLevelContainer ||
      !this.elements.intermediateLevelContainer ||
      !this.elements.thirdLevelContainer
    )
      return;

    this.focusTrap = new FocusTrap(this.elements.header);
    this.loadMenuData();
  }

  /**
   * JSONデータを読み込む
   */
  private async loadMenuData(): Promise<void> {
    try {
      const response = await fetch(this.jsonUrl);
      if (!response.ok) {
        throw new Error('Failed to load menu data');
      }
      this.menuData = await response.json();
      // _commentout: true のデータを削除
      this.menuData = this.filterMenuData(this.menuData);
      this.init();
    } catch (error) {
      console.error('Error loading menu data:', error);
    }
  }

  /**
   * _commentout: true のデータを再帰的に削除
   */
  private filterMenuData(menuData: HeaderMenu | null): HeaderMenu | null {
    if (!menuData) return null;
    return menuData.map((section) => ({
      ...section,
      links: section.links ? this.filterLinks(section.links) : undefined, // links を再帰的に処理
    }));
  }

  /**
   * _commentout: true のリンクを削除（再帰的処理）
   */
  private filterLinks(links: Link[]): Link[] {
    return links
      .filter((link) => !link._commentout) // リンクのフィルタリング
      .map((link) => ({
        ...link,
        links: link.links ? this.filterLinks(link.links) : undefined, // ネストされたリンクを処理
      }));
  }

  /**
   * ユニークなIDを生成するヘルパー関数
   */
  private generateUniqueId(prefix: string = 'menu'): string {
    return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private init(): void {
    // メニューコンテナにIDを割り当て
    this.assignMenuId();
    // メニューを生成
    this.createMenu();
    // ARIA属性の初期設定
    this.initializeAriaAttributes();
    // イベントリスナーを追加
    this.addEvents();
  }

  /**
   * メニューコンテナと各階層コンテナにIDを割り当てる
   */
  private assignMenuId(): void {
    const menuContainer = this.elements.menuContainer;
    if (!menuContainer || !this.menuData) return;

    // メインメニューコンテナのIDを設定
    if (menuContainer.id) {
      this.menuId = menuContainer.id;
    } else {
      this.menuId = this.generateUniqueId('hamburger-menu');
      menuContainer.id = this.menuId;
    }

    // 各階層のコンテナにIDを設定
    const levelContainers = [
      { element: this.elements.secondLevelContainer, suffix: 'level2' },
      {
        element: this.elements.intermediateLevelContainer,
        suffix: 'intermediate',
      },
      { element: this.elements.thirdLevelContainer, suffix: 'level3' },
    ];

    levelContainers.forEach(({ element, suffix }) => {
      if (element) {
        if (element.id) {
          // @ts-ignore
          this[`level${suffix.charAt(0).toUpperCase() + suffix.slice(1)}Id`] =
            element.id;
        } else {
          const generatedId = `${this.menuId}-${suffix}`;
          element.id = generatedId;
          // @ts-ignore
          this[`level${suffix.charAt(0).toUpperCase() + suffix.slice(1)}Id`] =
            generatedId;
        }
      }
    });
  }

  /**
   * 初期化時にARIA属性を設定
   */
  private initializeAriaAttributes(): void {
    const toggleButtons = document.querySelectorAll<HTMLElement>(
      '.ist-js__headerHamburgerMenuButton'
    );

    toggleButtons.forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', this.menuId);
      button.setAttribute('aria-label', 'メインメニューを開閉');
    });

    const menuContainer = this.elements.menuContainer;
    if (menuContainer) {
      menuContainer.setAttribute('aria-hidden', 'true');
      menuContainer.setAttribute('role', 'navigation');
    }

    // 各階層のコンテナに既にIDが設定されている場合は不要
    // ここではassignMenuIdで既にIDを設定しているため、再設定する必要はありません
  }

  public destroy(): void {
    this.eventListeners.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler);
    });
    this.eventListeners = [];

    if (this.elements.hamburgerMenuContent01) {
      this.elements.hamburgerMenuContent01.innerHTML = '';
    }
  }

  private addEventListenerWithCleanup<K extends keyof HTMLElementEventMap>(
    element: HTMLElement,
    event: K,
    handler: (ev: HTMLElementEventMap[K]) => void
  ): void {
    element.addEventListener(event, handler as EventListener);
    this.eventListeners.push({
      element,
      event,
      handler: handler as EventListener,
    });
  }

  /**
   * createMenu メソッドの修正バージョン
   * 既存の <li> 要素をクリアせずに、新しい <li> 要素を上に追加します
   */
  private createMenu(): void {
    const firstLevelList =
      this.elements.hamburgerMenuContent01?.querySelector<HTMLUListElement>(
        '.ist-header__hamburgerMenuList--01.ist-js__hamburgerMenuLink'
      );
    if (!firstLevelList) return;

    // DocumentFragmentを使用して一度に挿入することでパフォーマンスを向上
    const fragment = document.createDocumentFragment();

    this.menuData?.forEach((section: MenuSection, index: number) => {
      const menuItem = document.createElement('li');
      menuItem.className = 'ist-header__hamburgerMenuItem';
      let link: HTMLAnchorElement;

      if (section.link || section.link === '') {
        //link = `<a href="${section.link}" class="ist-header__hamburgerMenuLink">${section.title}</a>`;
        link = document.createElement('a');
        link.href = section.link;
        link.className = 'ist-header__hamburgerMenuLink';
        link.textContent = section.title;
      } else {
        // 第2階層のコンテナIDを参照するために使用
        const level2Id = `${this.menuId}-level2`;

        link = document.createElement('a');
        link.href = '#';
        link.className = 'ist-header__hamburgerMenuLink';
        link.dataset.sectionIndex = section.title;
        link.textContent = section.title;
        // 第1階層のリンクはすべて子メニューを持つので、必ずaria属性を設定
        link.setAttribute('aria-expanded', 'false');
        link.setAttribute('aria-controls', level2Id);
        link.id = `${this.menuId}-level1-item-${index}`;

        this.addEventListenerWithCleanup(link, 'click', (e: Event) => {
          e.preventDefault();

          // すべての第1階層リンクのaria-expandedをfalseに設定
          const parentList = link.closest<HTMLElement>(
            '.ist-header__hamburgerMenuList'
          );
          if (parentList) {
            parentList
              .querySelectorAll('.ist-header__hamburgerMenuLink')
              .forEach((menuLink) => {
                menuLink.setAttribute('aria-expanded', 'false');
              });

            // data-selected-menuを削除
            parentList
              .querySelectorAll('[data-selected-menu]')
              .forEach((element) => {
                element.removeAttribute('data-selected-menu');
              });
          }

          // クリックされたリンクのaria-expandedをtrueに設定
          link.setAttribute('aria-expanded', 'true');
          // クリックされたリンクに data-selected-menu を追加
          link.setAttribute('data-selected-menu', 'true');

          if (this.menuData) {
            this.showSecondLevel(this.menuData[index]);
          }
        });
      }

      menuItem.appendChild(link);
      fragment.appendChild(menuItem);
    });

    // 既存の最初の <li> の前に動的なメニューを挿入
    firstLevelList.insertBefore(fragment, firstLevelList.firstElementChild);
  }

  private addEvents(): void {
    const toggleButtons = document.querySelectorAll<HTMLElement>(
      '.ist-js__headerHamburgerMenuButton'
    );

    toggleButtons.forEach((button) => {
      this.addEventListenerWithCleanup(button, 'click', (e: Event) => {
        e.preventDefault();
        this.toggleMenu();
      });
    });

    this.addEventListenerWithCleanup(
      document as unknown as HTMLElement,
      'keydown',
      (e: KeyboardEvent) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.closeMenu();
        }
      }
    );
  }

  private showSecondLevel(section: MenuSection): void {
    if (!this.elements.menuContainer) return;

    this.elements.menuContainer.classList.add(
      'ist-header__hamburgerMenuContainer--secondLevel'
    );

    if (this.elements.menuBody) {
      this.elements.menuBody.scrollTop = 0;
    }

    const secondLevelContainer = this.elements.secondLevelContainer;
    if (!secondLevelContainer) return;

    // メニューのヘッダーを生成
    let bodyHeader = '';
    if (section.topLink) {
      bodyHeader = `
        <div class="ist-header__hamburgerMenuBodyHeader">
          <p class="ist-header__hamburgerMenuBodyHeaderTitle">${section.title}</p>
          <a href="${section.topLink.link}" class="ist-header__hamburgerMenuBodyHeaderLink">
            ${section.topLink.titleSp || section.topLink.title}
          </a>
        </div>
      `;
    }

    // バックリンクをSpNavListに追加
    const spNavList = this.elements.spNavList;
    if (spNavList) {
      const backLi = document.createElement('li');
      backLi.className =
        'ist-header__row01SpNavItem ist-header__row01SpNavItem--back';
      backLi.innerHTML = `<a href="#" class="ist-header__hamburgerMenuBack">${this.lang === 'en' ? 'All' : 'すべて'}</a>`;
      spNavList.insertBefore(backLi, spNavList.firstChild);

      const backButton = backLi.querySelector<HTMLElement>(
        '.ist-header__hamburgerMenuBack'
      );
      if (backButton) {
        this.addEventListenerWithCleanup(backButton, 'click', (e) => {
          e.preventDefault();
          if (this.elements.menuContainer) {
            this.elements.menuContainer.classList.remove(
              'ist-header__hamburgerMenuContainer--secondLevel'
            );
          }
          // 300ms後にコンテンツを削除
          setTimeout(() => {
            secondLevelContainer.innerHTML = '';
            backLi.remove(); // バックリンクを削除
          }, 300);
        });
      }
    }

    // intermediate層のコンテナIDを参照するために使用
    const intermediateId = `${this.menuId}-intermediate`;

    // メニューの中身を生成
    const body = section.links
      ? `
      <ul class="ist-header__hamburgerMenuList">
        ${section.links
          .map((link: Link, index: number) => {
            const hasSubMenu = link.links && link.links.length > 0;
            return `
              <li class="ist-header__hamburgerMenuItem">
                <a href="${link.link}" 
                   class="ist-header__hamburgerMenuLink ${link.isCaption ? 'ist-header__hamburgerMenuLink--caption' : ''}"
                   ${hasSubMenu ? `aria-expanded="false"` : ''}
                   ${hasSubMenu ? `aria-controls="${intermediateId}"` : ''}
                   data-has-children="${Boolean(hasSubMenu)}"
                   ${link.target ? `target="${link.target}"` : ''}>
                  ${link.title}
                </a>
              </li>
            `;
          })
          .join('')}
      </ul>
    `
      : '';

    // バナー情報の取得とフッター生成は変更なし
    const banners = section.banners || [];
    let footer = '';
    if (banners.length > 0) {
      footer = `
        <div class="ist-header__hamburgerMenuFooter">
          <ul class="ist-header__hamburgerMenuFooterList">
            ${banners
              .map(
                (banner) => `
              <li class="ist-header__hamburgerMenuFooterItem">
                <a href="${banner.link}" class="ist-header__hamburgerMenuFooterLink">
                  <figure class="ist-header__hamburgerMenuFooterFigure">
                    <img src="${banner.image}" alt="" class="ist-header__hamburgerMenuFooterImage">
                    <figcaption class="ist-header__hamburgerMenuFooterCaption">
                      ${banner.title}
                    </figcaption>
                  </figure>
                </a>
              </li>
            `
              )
              .join('')}
          </ul>
        </div>
      `;
    }

    // メニューの中身を生成
    secondLevelContainer.innerHTML = bodyHeader + body + footer;

    // リンククリック時の処理
    const menuLinks = secondLevelContainer.querySelectorAll<HTMLElement>(
      '.ist-header__hamburgerMenuLink'
    );
    menuLinks.forEach((link, index) => {
      if (link.getAttribute('data-has-children') === 'true') {
        this.addEventListenerWithCleanup(link, 'click', (e) => {
          e.preventDefault();

          const parentLink = section.links?.[index];

          // debug
          console.debug('デバッグ', parentLink);

          if (parentLink?.links) {
            // 先祖.ist-header__hamburgerMenuList の data-selected-menu を削除
            const menuList = link.closest<HTMLElement>(
              '.ist-header__hamburgerMenuList'
            );
            if (menuList) {
              menuList
                .querySelectorAll('.ist-header__hamburgerMenuLink')
                .forEach((menuLink) => {
                  menuLink.setAttribute('aria-expanded', 'false');
                });

              // data-selected-menuを削除
              menuList
                .querySelectorAll('[data-selected-menu]')
                .forEach((element) => {
                  element.removeAttribute('data-selected-menu');
                });
            }

            // クリックされたリンクのaria-expandedをtrueに設定
            link.setAttribute('aria-expanded', 'true');
            // クリックされたリンクに data-selected-menu を追加
            link.setAttribute('data-selected-menu', 'true');

            this.showIntermediateLevel(parentLink, section);
          }
        });
      }
    });
  }

  private showIntermediateLevel(parentLink: Link, section: MenuSection): void {
    if (!parentLink.links) return;

    if (this.elements.menuContainer) {
      this.elements.menuContainer.classList.remove(
        'ist-header__hamburgerMenuContainer--secondLevel'
      );
      this.elements.menuContainer.classList.add(
        'ist-header__hamburgerMenuContainer--intermediateLevel'
      );
    }

    // メニューのスクロール位置をリセット
    if (this.elements.menuBody) {
      this.elements.menuBody.scrollTop = 0;
    }

    const intermediateLevelContainer = this.elements.intermediateLevelContainer;
    if (!intermediateLevelContainer) return;

    // メニューのヘッダーを生成
    let bodyHeader = '';
    //if (parentLink.title) {
    //  bodyHeader = `
    //    <div class="ist-header__hamburgerMenuBodyHeader">
    //      <p class="ist-header__hamburgerMenuBodyHeaderTitle">${parentLink.title}</p>
    //    </div>
    //  `;
    //}

    // バックリンクの処理
    const spNavList = this.elements.spNavList;
    if (spNavList) {
      const existingBackButton = spNavList.querySelector(
        '.ist-header__row01SpNavItem--back'
      );
      if (existingBackButton) {
        existingBackButton.remove();
      }

      const backLi = document.createElement('li');
      backLi.className =
        'ist-header__row01SpNavItem ist-header__row01SpNavItem--back';
      backLi.innerHTML = `<a href="#" class="ist-header__hamburgerMenuBack">${section.title}</a>`;
      spNavList.insertBefore(backLi, spNavList.firstChild);

      const backButton = backLi.querySelector<HTMLElement>(
        '.ist-header__hamburgerMenuBack'
      );
      if (backButton) {
        this.addEventListenerWithCleanup(backButton, 'click', (e) => {
          e.preventDefault();
          // aria-expanded状態を更新
          const previousLink = document.querySelector(
            `[aria-controls="${this.intermediateId}"]`
          );
          if (previousLink) {
            previousLink.setAttribute('aria-expanded', 'false');
          }

          if (this.elements.menuContainer) {
            this.elements.menuContainer.classList.remove(
              'ist-header__hamburgerMenuContainer--intermediateLevel'
            );
            this.elements.menuContainer.classList.add(
              'ist-header__hamburgerMenuContainer--secondLevel'
            );
          }
          setTimeout(() => {
            intermediateLevelContainer.innerHTML = '';
            backLi.remove();

            // 第2階層へ戻る時のバックボタンを追加
            const newBackLi = document.createElement('li');
            newBackLi.className =
              'ist-header__row01SpNavItem ist-header__row01SpNavItem--back';
            newBackLi.innerHTML = `<a href="#" class="ist-header__hamburgerMenuBack">${this.lang === 'en' ? 'All' : 'すべて'}</a>`;
            spNavList.insertBefore(newBackLi, spNavList.firstChild);

            const newBackButton = newBackLi.querySelector<HTMLElement>(
              '.ist-header__hamburgerMenuBack'
            );
            if (newBackButton) {
              this.addEventListenerWithCleanup(newBackButton, 'click', (e) => {
                e.preventDefault();
                if (this.elements.menuContainer) {
                  this.elements.menuContainer.classList.remove(
                    'ist-header__hamburgerMenuContainer--secondLevel'
                  );
                }
                setTimeout(() => {
                  const secondLevelContainer =
                    this.elements.secondLevelContainer;
                  if (secondLevelContainer) {
                    secondLevelContainer.innerHTML = '';
                  }
                  newBackLi.remove();
                }, 300);
              });
            }
          }, 300);
        });
      }
    }

    // 第3層のコンテナIDを参照するために使用
    const level3Id = `${this.menuId}-level3`;

    // リンクリストの生成
    const linkList = `
      <ul class="ist-header__hamburgerMenuList">
        ${parentLink.links
          .map(
            (subLink: Link, index: number) => `
          <li class="ist-header__hamburgerMenuItem">
            <a href="${subLink.link}" 
               class="ist-header__hamburgerMenuLink ${subLink.isCaption ? 'ist-header__hamburgerMenuLink--caption' : ''}"
               ${subLink.links && subLink.links.length > 0 ? `aria-expanded="false"` : ''}
               ${subLink.links && subLink.links.length > 0 ? `aria-controls="${level3Id}"` : ''}
               data-has-children="${Boolean(subLink.links && subLink.links.length > 0)}"
               ${subLink.target ? `target="${subLink.target}"` : ''}>
              ${subLink.title}
            </a>
          </li>
        `
          )
          .join('')}
      </ul>
    `;

    // バナー情報と生成は変更なし
    const banners = section.banners || [];
    let footer = '';
    if (banners.length > 0) {
      footer = `
        <div class="ist-header__hamburgerMenuFooter">
          <ul class="ist-header__hamburgerMenuFooterList">
            ${banners
              .map(
                (banner) => `
              <li class="ist-header__hamburgerMenuFooterItem">
                <a href="${banner.link}" class="ist-header__hamburgerMenuFooterLink">
                  <figure class="ist-header__hamburgerMenuFooterFigure">
                    <img src="${banner.image}" alt="" class="ist-header__hamburgerMenuFooterImage">
                    <figcaption class="ist-header__hamburgerMenuFooterCaption">
                      ${banner.title}
                    </figcaption>
                  </figure>
                </a>
              </li>
            `
              )
              .join('')}
          </ul>
        </div>
      `;
    }

    intermediateLevelContainer.innerHTML = bodyHeader + linkList + footer;

    const menuLinks = intermediateLevelContainer.querySelectorAll<HTMLElement>(
      '.ist-header__hamburgerMenuLink'
    );
    menuLinks.forEach((link) => {
      if (link.getAttribute('data-has-children') === 'true') {
        this.addEventListenerWithCleanup(link, 'click', (e) => {
          e.preventDefault();
          // リンクのタイトルを取得
          const linkTitle = link.textContent?.trim();
          // タイトルに一致するリンクを探す
          const subLink = parentLink.links?.find((l) => l.title === linkTitle);

          if (!subLink) return;

          const hasChildren = link.getAttribute('data-has-children') === 'true';
          if (hasChildren && subLink.links && subLink.links.length > 0) {
            // 3階層目の表示処理
            menuLinks.forEach((menuLink) => {
              menuLink.setAttribute('aria-expanded', 'false');
              menuLink.removeAttribute('data-selected-menu');
            });

            link.setAttribute('aria-expanded', 'true');
            link.setAttribute('data-selected-menu', 'true');

            this.showThirdLevel(subLink, section, parentLink);
          } else if (subLink.link) {
            window.location.href = subLink.link;
          }
        });
      }
    });
  }

  private showThirdLevel(
    link: Link,
    section: MenuSection,
    parentLink: Link
  ): void {
    // メニューのスクロール位置をリセット
    if (this.elements.menuBody) {
      this.elements.menuBody.scrollTop = 0;
    }

    const thirdLevelContainer = this.elements.thirdLevelContainer;
    if (!thirdLevelContainer || !link.links) return;

    if (this.elements.menuContainer) {
      this.elements.menuContainer.classList.remove(
        'ist-header__hamburgerMenuContainer--intermediateLevel'
      );
      this.elements.menuContainer.classList.add(
        'ist-header__hamburgerMenuContainer--thirdLevel'
      );
    }

    // メニューのヘッダーを生成
    let bodyHeader = '';
    //if (link.title) {
    //  bodyHeader = `
    //    <div class="ist-header__hamburgerMenuBodyHeader">
    //      <p class="ist-header__hamburgerMenuBodyHeaderTitle">${link.title}</p>
    //      ${link.isCaption ? `<p class="ist-header__hamburgerMenuLink--caption">${link.title}</p>` : ''}
    //    </div>
    //  `;
    //}

    // バックリンクをSpNavListに追加
    const spNavList = this.elements.spNavList;
    if (spNavList) {
      const existingBackButton = spNavList.querySelector(
        '.ist-header__row01SpNavItem--back'
      );
      if (existingBackButton) {
        existingBackButton.remove();
      }

      const backLi = document.createElement('li');
      backLi.className =
        'ist-header__row01SpNavItem ist-header__row01SpNavItem--back';
      backLi.innerHTML = `<a href="#" class="ist-header__hamburgerMenuBack">${parentLink.title}</a>`;
      spNavList.insertBefore(backLi, spNavList.firstChild);

      const backButton = backLi.querySelector<HTMLElement>(
        '.ist-header__hamburgerMenuBack'
      );
      if (backButton) {
        this.addEventListenerWithCleanup(backButton, 'click', (e) => {
          e.preventDefault();
          // aria-expanded状態を更新
          const previousLink = document.querySelector(
            `[aria-controls="${this.level3Id}"]`
          );
          if (previousLink) {
            previousLink.setAttribute('aria-expanded', 'false');
          }

          if (this.elements.menuContainer) {
            this.elements.menuContainer.classList.remove(
              'ist-header__hamburgerMenuContainer--thirdLevel'
            );
            this.elements.menuContainer.classList.add(
              'ist-header__hamburgerMenuContainer--intermediateLevel'
            );
          }
          setTimeout(() => {
            thirdLevelContainer.innerHTML = '';
            backLi.remove();

            // Intermediate層のバックボタンを追加
            const newBackLi = document.createElement('li');
            newBackLi.className =
              'ist-header__row01SpNavItem ist-header__row01SpNavItem--back';
            newBackLi.innerHTML = `<a href="#" class="ist-header__hamburgerMenuBack">${section.title}</a>`;
            spNavList.insertBefore(newBackLi, spNavList.firstChild);

            const newBackButton = newBackLi.querySelector<HTMLElement>(
              '.ist-header__hamburgerMenuBack'
            );
            if (newBackButton) {
              this.addEventListenerWithCleanup(newBackButton, 'click', (e) => {
                e.preventDefault();
                const intermediatePreviousLink = document.querySelector(
                  `[aria-controls="${this.intermediateId}"]`
                );
                if (intermediatePreviousLink) {
                  intermediatePreviousLink.setAttribute(
                    'aria-expanded',
                    'false'
                  );
                }

                if (this.elements.menuContainer) {
                  this.elements.menuContainer.classList.remove(
                    'ist-header__hamburgerMenuContainer--intermediateLevel'
                  );
                  this.elements.menuContainer.classList.add(
                    'ist-header__hamburgerMenuContainer--secondLevel'
                  );
                }
                setTimeout(() => {
                  newBackLi.remove();

                  // 2階層の「すべて」バックボタンを生成
                  const secondLevelBackLi = document.createElement('li');
                  secondLevelBackLi.className =
                    'ist-header__row01SpNavItem ist-header__row01SpNavItem--back';
                  secondLevelBackLi.innerHTML = `<a href="#" class="ist-header__hamburgerMenuBack">${this.lang === 'en' ? 'All' : 'すべて'}</a>`;

                  if (spNavList) {
                    spNavList.insertBefore(
                      secondLevelBackLi,
                      spNavList.firstChild
                    );

                    const secondLevelBackButton =
                      secondLevelBackLi.querySelector<HTMLElement>(
                        '.ist-header__hamburgerMenuBack'
                      );
                    if (secondLevelBackButton) {
                      this.addEventListenerWithCleanup(
                        secondLevelBackButton,
                        'click',
                        (e) => {
                          e.preventDefault();
                          if (this.elements.menuContainer) {
                            this.elements.menuContainer.classList.remove(
                              'ist-header__hamburgerMenuContainer--secondLevel'
                            );
                          }
                          setTimeout(() => {
                            secondLevelBackLi.remove();
                          }, 300);
                        }
                      );
                    }
                  }
                }, 300);
              });
            }
          }, 300);
        });
      }
    }

    // リンクリストの生成
    const linkList = `
      <ul class="ist-header__hamburgerMenuList">
        ${link.links
          .map(
            (subLink: Link, index: number) => `
          <li class="ist-header__hamburgerMenuItem">
            <a href="${subLink.link}" 
               class="ist-header__hamburgerMenuLink ${subLink.isCaption ? 'ist-header__hamburgerMenuLink--caption' : ''}"
               id="${this.level3Id}-item-${index}"
               ${subLink.target ? `target="${subLink.target}"` : ''}>
              ${subLink.title}
            </a>
          </li>
        `
          )
          .join('')}
      </ul>
    `;

    // バナー情報と生成は変更なし
    const banners = section.banners || [];
    let footer = '';
    if (banners.length > 0) {
      footer = `
        <div class="ist-header__hamburgerMenuFooter">
          <ul class="ist-header__hamburgerMenuFooterList">
            ${banners
              .map(
                (banner) => `
              <li class="ist-header__hamburgerMenuFooterItem">
                <a href="${banner.link}" class="ist-header__hamburgerMenuFooterLink">
                  <figure class="ist-header__hamburgerMenuFooterFigure">
                    <img src="${banner.image}" alt="" class="ist-header__hamburgerMenuFooterImage">
                    <figcaption class="ist-header__hamburgerMenuFooterCaption">
                      ${banner.title}
                    </figcaption>
                  </figure>
                </a>
              </li>
            `
              )
              .join('')}
          </ul>
        </div>
      `;
    }

    thirdLevelContainer.innerHTML = bodyHeader + linkList + footer;

    // 必要に応じて、さらなるイベントリスナーの追加やフォーカストラップの管理を行います
  }

  private toggleMenu(): void {
    if (this.isOpen) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  private openMenu(): void {
    this.isOpen = true;

    // スクロール位置を保存
    this.scrollPosition =
      window.pageYOffset || document.documentElement.scrollTop;

    // position: sticky要素のための位置設定
    document.documentElement.style.setProperty(
      '--scroll-top',
      `${this.scrollPosition}px`
    );

    if (this.elements.header) {
      this.elements.header.classList.add('ist-header--menuOpen');
    }

    // ARIA属性を更新
    const toggleButtons = document.querySelectorAll<HTMLElement>(
      '.ist-js__headerHamburgerMenuButton'
    );
    const menuContainer = this.elements.menuContainer;
    if (toggleButtons && menuContainer) {
      toggleButtons.forEach((button) => {
        button.setAttribute('aria-expanded', 'true');
      });
      menuContainer.setAttribute('aria-hidden', 'false');
    }

    if (this.focusTrap) {
      this.focusTrap.activate();
    }
  }

  private closeMenu(): void {
    this.isOpen = false;

    // position: sticky要素の位置設定をリセット
    document.documentElement.style.removeProperty('--scroll-top');

    // スクロール位置を復元
    window.scrollTo(0, this.scrollPosition);

    // すべてのトリガーボタンのaria-expandedをfalseに
    const toggleButtons = document.querySelectorAll<HTMLElement>(
      '.ist-js__headerHamburgerMenuButton'
    );
    toggleButtons.forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });

    // メニューコンテナのaria-hiddenを設定
    if (this.elements.menuContainer) {
      this.elements.menuContainer.setAttribute('aria-hidden', 'true');

      // すべての階層のaria-expandedをリセット
      this.elements.menuContainer
        .querySelectorAll<HTMLElement>(
          '.ist-header__hamburgerMenuLink[aria-expanded]'
        )
        .forEach((link) => {
          link.setAttribute('aria-expanded', 'false');
        });

      // セカンド/中間/サードレベルのクラスを削除
      this.elements.menuContainer.classList.remove(
        'ist-header__hamburgerMenuContainer--secondLevel',
        'ist-header__hamburgerMenuContainer--intermediateLevel',
        'ist-header__hamburgerMenuContainer--thirdLevel'
      );
    }

    if (this.elements.header) {
      this.elements.header.classList.remove('ist-header--menuOpen');
    }

    // メニューのスクロール位置をリセット
    if (this.elements.menuBody) {
      this.elements.menuBody.scrollTop = 0;
    }

    // バックリンクを削除
    const backButton = document.querySelector<HTMLElement>(
      '.ist-header__row01SpNavItem--back'
    );
    if (backButton) {
      backButton.remove();
    }

    // 各階層のコンテンツをクリア
    const secondLevelContainer = document.querySelector<HTMLElement>(
      '.ist-header__hamburgerMenuBodyContentItem--02'
    );
    const intermediateLevelContainer = document.querySelector<HTMLElement>(
      '.ist-header__hamburgerMenuBodyContentItem--intermediate'
    );
    const thirdLevelContainer = document.querySelector<HTMLElement>(
      '.ist-header__hamburgerMenuBodyContentItem--03'
    );

    // 第二階層 メニューの中身を初期化
    if (secondLevelContainer) {
      secondLevelContainer.innerHTML = '';
    }

    // 中間階層 メニューの中身を初期化
    if (intermediateLevelContainer) {
      intermediateLevelContainer.innerHTML = '';
    }

    // 第三階層 メニューの中身を初期化
    if (thirdLevelContainer) {
      thirdLevelContainer.innerHTML = '';
    }

    // selected-menuデータ属性を削除
    document.querySelectorAll('[data-selected-menu]').forEach((element) => {
      element.removeAttribute('data-selected-menu');
    });

    // focusTrapを無効化
    if (this.focusTrap) {
      this.focusTrap.deactivate();
    }
  }
}

// ハンバーガーメニュー
export default HeaderHamburgerMenu;
